<template>
  <!-- `contents` wrapper — gives this multi-node template a single root so
  `v-show`/attrs from the parent (toggling collapsed vs expanded) land
  correctly, without affecting the flex-col stacking of the two children
  (`display: contents` removes the wrapper from layout entirely). -->
  <div class="contents">
    <div
      :aria-label="title"
      class="relative flex w-full cursor-pointer flex-col"
      role="button"
      tabindex="0"
      @click="emit('expand')"
      @keydown.enter="emit('expand')"
      @keydown.space.prevent="emit('expand')"
    >
      <LiveShoppingVideo
        ref="videoRef"
        :controls="false"
        loop
        :rounded="false"
        :title
        :video-id
      />

      <div
        class="mood-inverse absolute inset-x-0 top-0 flex items-center justify-between p-8"
      >
        <RevButtonIcon
          :alternative-text="i18n(translations.expandLabel)"
          class="drop-shadow-[0px_1px_4px_rgba(15,17,23,0.5)]"
          :icon="IconExpand"
          size="small"
          variant="ghost"
          @click.stop="emit('expand')"
        />
        <RevButtonIcon
          :alternative-text="i18n(translations.closeLabel)"
          class="drop-shadow-[0px_1px_4px_rgba(15,17,23,0.5)]"
          :icon="IconCrossInCircleFilled"
          size="small"
          variant="ghost"
          @click.stop="emit('dismiss')"
        />
      </div>

      <span class="flex items-center justify-between gap-8 p-8">
        <RevTag
          :label="i18n(translations.tagLabel)"
          size="small"
          variant="danger"
          variation="filled"
        />
        <!-- Count label hidden on mobile — the 160px collapsed pill is too
             narrow for "Live shopping" tag + "5 MacBooks" side by side. -->
        <span v-if="isDesktop" class="caption text-static-default-low">
          {{ productCountLabel }}
        </span>
      </span>
    </div>

    <!-- `max-h-[156px]` — ~1.5x a card's height (104px, matching the
    `RevSkeleton` height in LiveShoppingExpandedContent.vue) so the peek of a
    partial 2nd card hints there's more to see; scrolls internally rather
    than growing the collapsed pill itself. -->
    <div
      v-if="isDesktop && products.length > 0"
      class="flex max-h-[156px] flex-col gap-8 overflow-y-auto p-8 pt-0"
    >
      <LiveShoppingProductCard
        v-for="(product, index) in products"
        :key="product.objectId"
        :product
        @add-to-cart="emit('add-to-cart', product, index)"
        @jump-to-moment="emit('jump-to-moment', product, index)"
        @product-click="emit('product-click', product, index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevTag } from '@ds/components/Tag'
import { IconCrossInCircleFilled } from '@ds/icons/IconCrossInCircleFilled'
import { IconExpand } from '@ds/icons/IconExpand'

import type { LiveShoppingProduct } from '../composables/useLiveShoppingProducts'

import LiveShoppingProductCard from './LiveShoppingProductCard.vue'
import LiveShoppingVideo from './LiveShoppingVideo.vue'
import translations from './LiveShoppingWidget.translations'

defineProps<{
  isDesktop: boolean
  /**
   * Pre-translated by the widget per category ("5 iPads" / "4 MacBooks") —
   * the children don't know the category, only how to display the label.
   */
  productCountLabel: string
  products: LiveShoppingProduct[]
  title: string
  videoId: string
}>()

const emit = defineEmits<{
  (e: 'expand'): void
  (e: 'dismiss'): void
  (e: 'jump-to-moment', product: LiveShoppingProduct, position: number): void
  (e: 'product-click', product: LiveShoppingProduct, position: number): void
  (e: 'add-to-cart', product: LiveShoppingProduct, position: number): void
}>()

const i18n = useI18n()

const videoRef = useTemplateRef('videoRef')

function seekTo(seconds: number) {
  videoRef.value?.seekTo(seconds)
}

function pause() {
  return videoRef.value?.pause() ?? false
}

function resume() {
  videoRef.value?.resume()
}

defineExpose({ seekTo, pause, resume })
</script>
